import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AACChromeAPI from '@amzn/aac-chrome-api';
import {
  selectEmbeddedChatOpen,
  setEmbeddedChatOpen,
  A3cChatConfig,
} from 'src/app/slice';
import {
  initA3CEmbeddedConfig,
  createEmbeddedChatContainer,
  setEmbeddedChatVisible,
  setCurrentFocusMode,
  handleEmbeddedChatResize,
  setFlyoverPlacement,
  EmbeddedChatPlacement,
  A3C_CONTAINER_ID,
} from 'src/commons/utils/a3cEmbeddedChat';
import {
  isUcbEverywherePath,
  isChatDisabledUrl,
  isEmbeddedModeUrl,
} from 'src/commons/utils/a3cChatUrlRules';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import {
  A3C_LOAD_BEHAVIOR_DISABLED,
  WeblabedFeatures,
} from 'src/commons/constants';
import { updatePageContext } from '@amzn/a3c-runtime-sdk';
import {
  subscribeToAdsAgentChatEvents,
  AdsAgentChatEventType,
} from '@amzn/a3-chat-event-sdk';

const A3C_EVERYWHERE_DEFAULT_AGENT = 'ads_agent_orchestrator';

interface EmbeddedChatManagerProps {
  isFocusMode: boolean;
}

/**
 * Pure side-effect component that manages embedded chat lifecycle:
 * - Registers the bidirectional toggle callback on ChromeConfiguration
 * - Initializes A3CConfig and creates the chat container
 * - Syncs chat visibility with Redux state
 *
 * Renders nothing. Only mounted on desktop (non-mobile).
 */
export const EmbeddedChatManager: React.VFC<EmbeddedChatManagerProps> = ({
  isFocusMode,
}) => {
  const dispatch = useDispatch();
  const isEmbeddedChatOpen = useSelector(selectEmbeddedChatOpen);
  const chromeConfig = useChromeConfigContext();
  // A page-provided embedded config always wins over the flyover default.
  // Object presence is the signal: pages may legitimately set
  // embeddedChatConfig with an empty selectedAgentIdentifier (chat hidden),
  // and those setups must stay in embedded mode, not flip to flyover.
  const pageEmbeddedConfig = chromeConfig?.a3cConfig?.embeddedChatConfig;
  const pageAgentIdentifier = pageEmbeddedConfig?.selectedAgentIdentifier;
  const flyoverChatConfig = chromeConfig?.a3cConfig?.flyoverChatConfig;
  const isA3cEverywhereT1Enabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE,
  );
  const isA3cEverywhereT2Enabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE_T2,
  );
  const isA3cEverywhereUcbEnabled = useIsFeatureEnabled(
    WeblabedFeatures.A3C_EVERYWHERE_UCB,
  );
  // UCB everywhere paths (/cb campaign builders and /create/settings/st)
  // dial up on their own weblab: there, A3C_EVERYWHERE_UCB_1459740 is the
  // SOLE control of the everywhere experience (default agent, config
  // stamping) and A3C_EVERYWHERE_1451089 T1/T2 have no effect. Everywhere
  // else T1/T2 control it as before.
  const isA3cEverywhereEnabled = isUcbEverywherePath(window.location.pathname)
    ? isA3cEverywhereUcbEnabled
    : isA3cEverywhereT1Enabled || isA3cEverywhereT2Enabled;
  // URL rules are static for the lifetime of a page load.
  const isEmbeddedModeForcedByUrl = isEmbeddedModeUrl(window.location.href);
  const isEmbeddedConfigActive = !!pageEmbeddedConfig;
  // A3C is fully disabled on this page when either the page opted out via
  // loadBehavior DISABLED or the URL is on the chat-disabled list: no
  // container, no agent, no injection — even under A3C_EVERYWHERE.
  const isA3cDisabled =
    chromeConfig?.a3cConfig?.loadBehavior === A3C_LOAD_BEHAVIOR_DISABLED ||
    isChatDisabledUrl(window.location.href);
  // Under A3C_EVERYWHERE the chat overlays the page (flyover) instead of
  // squeezing it: T1 anchors next to the nav rail (inline-start), T2 anchors
  // to the opposite edge (inline-end) like the legacy A3ChatAssets flyover.
  // Flyover only applies when the flyover config is active: pages with their
  // own embedded config keep the legacy push behavior, and forced-embedded
  // URLs stay embedded (with the everywhere agent) even under the weblab.
  let flyoverPlacement: EmbeddedChatPlacement | null = null;
  if (!isEmbeddedConfigActive && !isEmbeddedModeForcedByUrl) {
    if (isA3cEverywhereT1Enabled) {
      flyoverPlacement = 'inline-start';
    } else if (isA3cEverywhereT2Enabled) {
      flyoverPlacement = 'inline-end';
    }
  }
  // Agent precedence: an embedded config's own agent always wins, including
  // an explicit empty string (chat hidden). The everywhere default applies
  // when the agent is not set — configs arrive as parsed JSON, so a null
  // value (serializers emit null for unset fields) counts as not set too.
  // Forced-embedded URLs resolve through the embedded branch (matching the
  // embeddedChatConfig injection), never from flyoverChatConfig.
  let effectiveAgentIdentifier: string | undefined;
  if (isA3cDisabled) {
    effectiveAgentIdentifier = undefined;
  } else if (isEmbeddedConfigActive || isEmbeddedModeForcedByUrl) {
    effectiveAgentIdentifier =
      pageAgentIdentifier != null
        ? pageAgentIdentifier || undefined
        : isA3cEverywhereEnabled
          ? A3C_EVERYWHERE_DEFAULT_AGENT
          : undefined;
  } else {
    effectiveAgentIdentifier = isA3cEverywhereEnabled
      ? flyoverChatConfig?.selectedAgentIdentifier ||
        A3C_EVERYWHERE_DEFAULT_AGENT
      : undefined;
  }
  const panelMode =
    isEmbeddedConfigActive || isEmbeddedModeForcedByUrl
      ? pageEmbeddedConfig?.panelMode
      : flyoverChatConfig?.panelMode;
  const defaultWidth = panelMode === 'compact' ? 475 : 675;

  // One-time setup: register callbacks, init A3CConfig, and create the container.
  // isFocusMode, dispatch, and flyoverPlacement are stable after page load,
  // so this runs essentially once. Skipped entirely when A3C is disabled.
  useEffect(() => {
    if (isA3cDisabled) {
      return;
    }
    setCurrentFocusMode(isFocusMode);
    setFlyoverPlacement(flyoverPlacement);
    initA3CEmbeddedConfig({
      closable: !isFocusMode,
      onStateChange: (isOpen) => {
        dispatch(setEmbeddedChatOpen(isOpen));
      },
    });
    createEmbeddedChatContainer(isFocusMode);

    const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
    // Skip if the page's AACChromeAPI bundle is not the latest version.
    if (
      typeof chromeConfiguration.setOnEmbeddedChatToggle === 'function' &&
      typeof chromeConfiguration.setOnEmbeddedChatResize === 'function'
    ) {
      chromeConfiguration.setOnEmbeddedChatToggle((isOpen: boolean) => {
        dispatch(setEmbeddedChatOpen(isOpen));
      });
      chromeConfiguration.setOnEmbeddedChatResize((width: number) => {
        handleEmbeddedChatResize(width, isFocusMode);
      });
      AACChromeAPI.setChromeConfiguration(chromeConfiguration);
    }
  }, [isFocusMode, dispatch, flyoverPlacement, isA3cDisabled]);

  // Inject the default config once (other components and A3ChatAssets read
  // from it). Pages with an existing embeddedChatConfig — and forced-embedded
  // URLs — receive defaults merged INTO embeddedChatConfig (never a
  // flyoverChatConfig); only pages with no embedded config receive a default
  // flyoverChatConfig. Defaults are spread FIRST so any page-provided values
  // override them — including an explicit empty selectedAgentIdentifier.
  useEffect(() => {
    if (isA3cEverywhereEnabled && !isA3cDisabled) {
      const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
      if (typeof chromeConfiguration.setA3cConfig === 'function') {
        const existing = chromeConfiguration.getA3cConfig?.();
        if (isEmbeddedConfigActive || isEmbeddedModeForcedByUrl) {
          const mergedEmbeddedConfig: A3cChatConfig = {
            selectedAgentIdentifier: A3C_EVERYWHERE_DEFAULT_AGENT,
            ...existing?.embeddedChatConfig,
          };
          // A null agent (JSON serializers emit null for unset fields) counts
          // as not set — apply the default. An explicit '' is respected.
          mergedEmbeddedConfig.selectedAgentIdentifier ??=
            A3C_EVERYWHERE_DEFAULT_AGENT;
          // An injected config (no page-provided embedded config, i.e. a
          // forced-embedded URL) is the default everywhere chat: under the
          // A3C_EVERYWHERE (T1) weblab, mark it limited. T1 off (T2- or
          // UCB-driven injection): no chatMode is stamped, which resolves to
          // 'full' downstream in A3ChatAssets. A page-provided config always
          // keeps its own chatMode.
          if (!existing?.embeddedChatConfig && isA3cEverywhereT1Enabled) {
            mergedEmbeddedConfig.chatMode = 'limited';
          }
          // containerId is auto-stamped by AACChromeAPI for embeddedChatConfig.
          chromeConfiguration.setA3cConfig({
            ...existing,
            embeddedChatConfig: mergedEmbeddedConfig,
          });
        } else {
          const mergedFlyoverConfig: A3cChatConfig = {
            selectedAgentIdentifier: A3C_EVERYWHERE_DEFAULT_AGENT,
            containerId: A3C_CONTAINER_ID,
            ...existing?.flyoverChatConfig,
          };
          // The flyover experience always has an agent: normalize any falsy
          // value (null, undefined, '') to the everywhere default so the
          // stamped config matches the rendered behavior.
          if (!mergedFlyoverConfig.selectedAgentIdentifier) {
            mergedFlyoverConfig.selectedAgentIdentifier =
              A3C_EVERYWHERE_DEFAULT_AGENT;
          }
          // An injected config (no page-provided flyover config) is the
          // default everywhere chat: under the A3C_EVERYWHERE (T1) weblab,
          // mark it limited. T1 off (T2-driven injection): no chatMode is
          // stamped, which resolves to 'full' downstream. A page-provided
          // config always keeps its own chatMode.
          if (!existing?.flyoverChatConfig && isA3cEverywhereT1Enabled) {
            mergedFlyoverConfig.chatMode = 'limited';
          }
          chromeConfiguration.setA3cConfig({
            ...existing,
            flyoverChatConfig: mergedFlyoverConfig,
          });
        }
        AACChromeAPI.setChromeConfiguration(chromeConfiguration);
      }
      // Set orchestratorVariant so requests route through AAO v2 — but only
      // when the everywhere default agent actually applies. Pages that set
      // their own agent (or an explicit empty one) keep their routing. Gated
      // on the effective agent, not the config stamp, so older AACChromeAPI
      // bundles (no setA3cConfig) still route the default agent correctly.
      // Wait for CHAT_READY (sticky) to ensure the runtime is loaded.
      if (effectiveAgentIdentifier === A3C_EVERYWHERE_DEFAULT_AGENT) {
        subscribeToAdsAgentChatEvents(AdsAgentChatEventType.CHAT_READY, () => {
          try {
            updatePageContext({ orchestratorVariant: 'graph_orchestration' });
          } catch {
            // Runtime not available — fail silently
          }
        });
      }
    }
  }, [
    isEmbeddedConfigActive,
    isA3cEverywhereEnabled,
    isA3cEverywhereT1Enabled,
    isEmbeddedModeForcedByUrl,
    isA3cDisabled,
    effectiveAgentIdentifier,
  ]);

  // Update closable state and chat panel visibility when dependencies change.
  useEffect(() => {
    if (effectiveAgentIdentifier) {
      const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
      if (typeof chromeConfiguration.setEmbeddedChatClosable === 'function') {
        chromeConfiguration.setEmbeddedChatClosable(!isFocusMode);
        AACChromeAPI.setChromeConfiguration(chromeConfiguration);
      }
    }

    setEmbeddedChatVisible(
      !!effectiveAgentIdentifier && isEmbeddedChatOpen,
      defaultWidth,
      isFocusMode,
    );
    // Notify A3ChatAssets of panel visibility change (P411687905)
    try {
      window.A3CConfig?.onVisibilityChange?.(
        !!effectiveAgentIdentifier && isEmbeddedChatOpen,
      );
    } catch (e) {
      // Log so external-code failures are observable without breaking Chrome UI
      console.warn('A3CConfig.onVisibilityChange failed', e);
    }
  }, [isFocusMode, effectiveAgentIdentifier, isEmbeddedChatOpen]);

  return null;
};
